import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../../lib/api/_request.js';
import type { SpecFlow } from '../../../lib/api/spec-requirements.js';

/**
 * レビュー状態の表示名の翻訳キーと色。鍵は API が返す状態の値そのもの（識別子なので訳さない）。
 * @graph-connects none
 */
const STATUS: Record<string, { labelKey: string; className: string }> = {
  requested: {
    labelKey: 'spec.flow.status.requested',
    className:
      'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] border-[var(--color-border)]',
  },
  ai_changes_requested: {
    labelKey: 'spec.flow.status.aiChangesRequested',
    className:
      'bg-[var(--color-warning-solid)] text-[var(--color-warning-text)] border-[var(--color-warning)]',
  },
  ai_approved: {
    labelKey: 'spec.flow.status.aiApproved',
    className:
      'bg-[var(--color-success-solid)] text-[var(--color-success-text)] border-[var(--color-success)]',
  },
  dle_changes_requested: {
    labelKey: 'spec.flow.status.dleChangesRequested',
    className:
      'bg-[var(--color-warning-solid)] text-[var(--color-warning-text)] border-[var(--color-warning)]',
  },
  approved: {
    labelKey: 'spec.flow.status.approved',
    className:
      'bg-[var(--color-success-solid)] text-[var(--color-success-text)] border-[var(--color-success)]',
  },
};

/** 取り下げ・解除の操作。入口が取り下げの可否を判定せず、API の応答をそのまま見せる。 @graph-connects none */
export interface FlowRetireControls {
  pending: boolean;
  /** 直前の取り下げ・解除が失敗した原因 */
  error: unknown;
  onRetire: (reason: string) => void;
  onRestore: () => void;
}

/**
 * 取り下げに失敗した理由。API が返したメッセージはそのまま見せる。
 * @graph-connects ApiError [references] 失敗理由の判別
 */
function retireErrorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) return error.message;
  return String(error);
}

/**
 * 取り下げの操作欄。有効な図には理由の入力つきの「取り下げる」、取り下げ済みには解除を出す。
 * @graph-connects ApiError [references] 失敗理由の判別
 */
function RetireControls({ flow, controls }: { flow: SpecFlow; controls: FlowRetireControls }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const failure = controls.error ?? null;

  if (flow.retired != null) {
    return (
      <div className="mt-3">
        <button
          type="button"
          className="text-xs px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
          aria-label={t('spec.flow.restoreAria', { title: flow.title })}
          disabled={controls.pending}
          onClick={controls.onRestore}
        >
          {t('spec.flow.restore')}
        </button>
        {failure !== null ? (
          <p role="alert" className="text-xs text-[var(--color-danger-text)] mt-1">
            {retireErrorMessage(failure)}
          </p>
        ) : null}
      </div>
    );
  }

  if (!open) {
    return (
      <div className="mt-3">
        <button
          type="button"
          className="text-xs px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
          aria-label={t('spec.flow.retireAria', { title: flow.title })}
          onClick={() => {
            setOpen(true);
          }}
        >
          {t('spec.flow.retire')}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 border border-[var(--color-border)] rounded p-3">
      <p className="text-xs text-[var(--color-text-muted)]">{t('spec.flow.retireHint')}</p>
      <label className="block text-xs mt-2">
        {t('spec.flow.retireReason')}
        <textarea
          className="mt-1 w-full border border-[var(--color-border)] rounded p-2 text-sm bg-[var(--color-bg-surface)]"
          rows={2}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
        />
      </label>
      {failure !== null ? (
        <p role="alert" className="text-xs text-[var(--color-danger-text)] mt-1">
          {retireErrorMessage(failure)}
        </p>
      ) : null}
      <div className="flex gap-2 mt-2">
        <button
          type="button"
          className="text-xs px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
          disabled={controls.pending}
          onClick={() => {
            controls.onRetire(reason);
          }}
        >
          {t('spec.flow.retireConfirm')}
        </button>
        <button
          type="button"
          className="text-xs px-3 py-1 border border-[var(--color-border)] rounded cursor-pointer disabled:cursor-not-allowed"
          disabled={controls.pending}
          onClick={() => {
            setOpen(false);
          }}
        >
          {t('spec.flow.retireCancel')}
        </button>
      </div>
    </div>
  );
}

/**
 * Mermaid の図。**既定は高さを抑えた縮小版**で、細部はクリックして拡大して見る。
 * 一覧として並ぶ場所なので、既定で大きく出すと 1 本の図で画面が埋まって他が見えなくなる。
 * mermaid は重いのでこの画面を開いたときだけ読み込む。
 * @graph-connects none
 */
export function Diagram({ source, onZoom }: { source: string; onZoom: (svg: string) => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState('');

  useEffect(() => {
    let alive = true;
    void (async () => {
      const mermaid = (await import('mermaid')).default;
      mermaid.initialize({ startOnLoad: false, theme: 'neutral' });
      const id = `flow-${Math.random().toString(36).slice(2)}`;
      const { svg: rendered } = await mermaid.render(id, source);
      if (alive) setSvg(rendered);
    })();
    return () => {
      alive = false;
    };
  }, [source]);

  if (svg === '')
    return (
      <div className="text-sm text-[var(--color-text-muted)] py-4">{t('spec.flow.rendering')}</div>
    );
  return (
    // 下地の `bg-white` は図の地。mermaid は `theme: 'neutral'`（節点の塗りは白・枠と矢印と
    // 文字はほぼ黒）で描くので、暗い面に置くと矢印と文字が消える。図の中の色は業務フローの
    // データ（`classDef`）側で決まるため、ここで固定するのは地だけにする。
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      className="border border-[var(--color-border)] rounded-lg p-3 bg-white cursor-zoom-in overflow-x-auto [&_svg]:max-h-64 [&_svg]:w-auto [&_svg]:mx-auto"
      onClick={() => {
        onZoom(svg);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onZoom(svg);
        }
      }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

/**
 * 図を大きく見るダイアログ。高さいっぱいまで拡大し、はみ出した分は横スクロールする。
 *
 * **覆いは `body` の直下へ `createPortal` で出し、層は `--z-modal` で決める。** 共有シェル
 * の本文ラッパー（`AppShell`）は登場アニメーション後も `transform: translateY(0)` を残し、
 * それが `position: fixed` の包含ブロックになる。木の中に置いたままだと、覆いはページの
 * スクロール位置に連れて動き本文の枠に閉じ込められ、サイドバー（`--z-sticky`=100）の領域まで
 * 届かない。同じ罠を spec-console の `flow-fullscreen.tsx` が先に踏んでいる。
 * @graph-connects none
 */
export function ZoomDialog({ svg, onClose }: { svg: string; onClose: () => void }) {
  const { t } = useTranslation();
  const body = useRef<HTMLDivElement>(null);

  // mermaid は svg に style="max-width: Npx" をインラインで付ける。インラインはクラスに
  // 勝つので、`max-w-none` が効かず図は元の幅のまま縦を余らせる。**サイズはクラスで決めたい**
  // ので、mermaid が付けた指定（style と width / height 属性）を剥がすだけにする。
  // 縦横比は viewBox が持つので、剥がしても崩れない。
  useLayoutEffect(() => {
    const el = body.current?.querySelector('svg');
    if (!el) return;
    for (const name of ['style', 'width', 'height']) el.removeAttribute(name);
  }, [svg]);

  return createPortal(
    // 覆い（scrim）の `bg-black/55` は共有デザインシステムの Modal の覆いと同じ考え方で、
    // 両テーマとも暗いベールであることに意味があるためテーマ変数にしない。
    <div
      className="fixed inset-0 z-[var(--z-modal)] bg-black/55 flex items-center justify-center p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="bg-[var(--color-bg-surface)] rounded-xl w-full max-w-[96vw] h-[92vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label={t('spec.flow.zoomAria')}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="flex justify-between items-center px-4 py-2 border-b border-[var(--color-border)]">
          <span className="font-medium">{t('spec.flow.zoomTitle')}</span>
          <button
            type="button"
            className="text-sm px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
            onClick={onClose}
          >
            {t('spec.action.close')}
          </button>
        </div>
        {/* 縮小版と同じ理由で、図の地だけは明色（`bg-white`）に固定する。
            角丸はダイアログ側の `rounded-xl` に合わせて下端だけ残す */}
        <div
          ref={body}
          className="flex-1 overflow-auto p-4 bg-white rounded-b-xl [&_svg]:h-full [&_svg]:w-auto [&_svg]:max-w-none"
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>
    </div>,
    document.body,
  );
}

/**
 * 業務フロー 1 件。見出し・前後の接続・図・この図への指摘・操作、の順に出す。
 * 操作部分（`actions`）の中身は呼び出し元が組み立てるので、ここでは `ReviewActions` を知らない。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 業務フロー 1 件を見出し・前後の接続・Mermaid 図・レビュー状態バッジ付きで表示し、
 *   編集モードへの切り替え・編集フォームの表示・保存後の機械チェック注意（warnings）表示を担う
 * @graph-connects none
 */
export function FlowSection({
  flow,
  onZoom,
  actions,
  editor,
  editing,
  onEdit,
  warnings,
  retire,
}: {
  flow: SpecFlow;
  onZoom: (svg: string) => void;
  actions: React.ReactNode;
  /** 編集中に出す入力欄。編集していなければ null */
  editor: React.ReactNode;
  editing: boolean;
  onEdit: () => void;
  /** 直前の保存で機械チェックが検知した注意。編集中は隠す */
  warnings: string[];
  /** 取り下げ・解除の操作。渡さなければ出さない */
  retire?: FlowRetireControls;
}) {
  const { t } = useTranslation();
  const retired = flow.retired != null;
  const review = flow.review;
  const badge = review ? (STATUS[review.status] ?? null) : null;
  const stale = review !== null && review.targetVersion !== flow.version;
  return (
    <section className="mt-6 border border-[var(--color-border)] rounded-lg p-4 bg-[var(--color-bg-surface)]">
      <div className="flex items-baseline gap-3 flex-wrap">
        <h3 className="text-base font-semibold">{flow.title}</h3>
        <span className="text-xs text-[var(--color-text-muted)] font-mono">v{flow.version}</span>
        {editing || retired ? null : (
          <button
            type="button"
            className="text-xs px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] ml-auto cursor-pointer disabled:cursor-not-allowed"
            aria-label={t('spec.flow.editAria', { title: flow.title })}
            onClick={onEdit}
          >
            {t('spec.action.edit')}
          </button>
        )}
        {retired ? (
          <span className="text-xs px-2 py-0.5 rounded-full border bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] border-[var(--color-border)] ml-auto">
            {t('spec.flow.retired')}
          </span>
        ) : badge ? (
          <span className={`text-xs px-2 py-0.5 rounded-full border ${badge.className}`}>
            {t(badge.labelKey)}
            {stale ? t('spec.flow.reviewAtVersion', { version: review.targetVersion }) : ''}
          </span>
        ) : (
          <span className="text-xs px-2 py-0.5 rounded-full border bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] border-[var(--color-border)]">
            {t('spec.flow.notRequested')}
          </span>
        )}
      </div>
      {flow.retired != null ? (
        <p className="text-xs text-[var(--color-text-secondary)] mt-1">
          {t('spec.flow.retiredBy', { by: flow.retired.by, reason: flow.retired.reason })}
        </p>
      ) : null}
      <p className="text-xs text-[var(--color-text-muted)] mt-1">
        {t('spec.flow.beforeAfter', { before: flow.before, after: flow.after })}
      </p>
      {!editing && warnings.length > 0 ? (
        <div className="mt-3 border-l-4 border-[var(--color-warning)] bg-[var(--color-warning-muted)] px-4 py-3 rounded-r">
          <h3 className="text-xs font-semibold text-[var(--color-warning-text)]">
            {t('spec.savedWithWarnings')}
          </h3>
          <ul className="mt-1 text-sm list-disc pl-5 text-[var(--color-text-primary)]">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {editing ? (
        editor
      ) : (
        <div className={retired ? 'mt-3 opacity-50' : 'mt-3'}>
          {/* 訳が無い・古いときは原文の図が返る。読んでいるものが原文だと分かるように断る */}
          {flow.untranslated === true ? (
            <p className="text-xs text-[var(--color-text-muted)] mb-1">
              {t('spec.flow.untranslated')}
            </p>
          ) : null}
          <Diagram source={flow.mermaid} onZoom={onZoom} />
          <p className="text-xs text-[var(--color-text-muted)] mt-1">{t('spec.flow.zoomHint')}</p>
          {flow.steps !== undefined && flow.steps.length > 0 ? (
            <details className="mt-2 text-xs text-[var(--color-text-secondary)]">
              <summary className="cursor-pointer">{t('spec.flow.legendTitle')}</summary>
              <p className="mt-1">{t('spec.flow.legendF')}</p>
              <p>{t('spec.flow.legendP')}</p>
              <ul className="mt-1 list-disc pl-5">
                {flow.steps.map((step) => (
                  <li key={step.id}>{t('spec.flow.legendStep', { label: step.label })}</li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      )}
      {review !== null && review.carriedVersions.length > 0 ? (
        <p className="text-xs text-[var(--color-text-muted)] mt-2">
          {t('spec.flow.carried', {
            versions: review.carriedVersions.map((v) => `v${String(v)}`).join(' / '),
          })}
        </p>
      ) : null}
      {retired ? null : actions}
      {retire !== undefined && !editing ? <RetireControls flow={flow} controls={retire} /> : null}
    </section>
  );
}
